'use client'
import { type FC, memo, useMemo } from 'react'
import styled from 'styled-components'
import { MODALS } from '@/context'
import { useModal, useTranslator } from '@/hooks'
import { Link } from '../Link'
import { createUnderline, font18, mq, rgbAlpha, vw } from '@/styles'


const colorByIndex = {
	'cta_0': '#F3DCDB',
	'cta_1': '#E9EFE8',
	'cta_2': '#FFEEE6',
}

export const ContentUserJourney:FC = memo(() => {
	const { t } = useTranslator()
	const { toggle:toggleStandaloneModal } = useModal(MODALS.USER_JOURNEY_STANDALONE)
	const { toggle:toggleCasambiModal } = useModal(MODALS.USER_JOURNEY_CASAMBI)
	const { toggle:toggleDaliDt8Modal } = useModal(MODALS.USER_JOURNEY_DALI_DT8)

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data = useMemo(() => t('project.user_journey.list') as unknown as any[], [t])

	return (
		<Div>
			<hgroup>
				<h2>User journey</h2>
			</hgroup>
			<Ul>
				{data.map((item, idx) => (
					<li key={idx}>
						<h2>{item.title}</h2>
						<div dangerouslySetInnerHTML={{ __html: item.description as string }} />
						<div className='ctas'>
							{item.ctas.map((cta, index) => {
								if(cta.type === 'internal-link') {
									return <Link key={index} href={cta.href} className={cta.underline ? 'underline' : ''}>{cta.label}</Link>
								} else if(cta.type === 'id-link') {
									return <a key={index} href={`#${cta.href}`} className={cta.underline ? 'underline' : ''}>
										{idx === 1 && <span className='color' style={{ backgroundColor: colorByIndex[`cta_${index}`] }}></span>}
										{cta.label}
									</a>
								} else if(cta.type === 'modal') {
									if(cta.modal_id === MODALS.USER_JOURNEY_STANDALONE) {
										return <button key={index} onClick={toggleStandaloneModal}>
											<span className='title'>{cta.title}</span>
											<div className='description' dangerouslySetInnerHTML={{ __html: cta.description as string }} />
											<span className='label'>{cta.label}</span>
										</button>
									} else if(cta.modal_id === MODALS.USER_JOURNEY_CASAMBI) {
										return <button key={index} onClick={toggleCasambiModal}>
											<span className='title'>{cta.title}</span>
											<div className='description' dangerouslySetInnerHTML={{ __html: cta.description as string }} />
											<span className='label'>{cta.label}</span>
										</button>
									} else if(cta.modal_id === MODALS.USER_JOURNEY_DALI_DT8) {
										return <button key={index} onClick={toggleDaliDt8Modal}>
											<span className='title'>{cta.title}</span>
											<div className='description' dangerouslySetInnerHTML={{ __html: cta.description as string }} />
											<span className='label'>{cta.label}</span>
										</button>
									}
								}
							})}
						</div>
					</li>
				))}
			</Ul>
		</Div>
	)
})


const Div = styled.div`
	border-bottom: 1px solid ${rgbAlpha('--color-text', .6)};
	grid-column: 1 / span 6;
	padding-bottom: ${vw(100, 'mobile')};
	padding-top: ${vw(100, 'mobile')};

	${mq.greaterThan('nexus7')} {
		padding-bottom: ${vw(100, 'tablet')};
		padding-top: ${vw(100, 'tablet')};
	}

	${mq.greaterThan('tablet')} {
		grid-column: 1 / span 12;
		padding-bottom: ${vw(130, 'desktop')};
		padding-top: ${vw(130, 'desktop')};
	}

	${mq.greaterThan('desktop')} {
		padding-bottom: 130px;
		padding-top: 130px;
	}

	hgroup {
		border-bottom: 1px solid var(--color-text);
		margin-bottom: 30px;
		padding-bottom: 40px;
		width: 100%;

		${mq.greaterThan('tablet')} {
			margin-bottom: 50px;
			padding-bottom: 70px;
		}

		h2 {
			font-size: 44px !important;
			letter-spacing: -.1px !important;
			line-height: 48px !important;	

			${mq.greaterThan('tablet')} {
				font-size: 90px !important;
				letter-spacing: -.1px !important;
				line-height: 94px !important;	
			}
		}
	}
`
const Ul = styled.ul`
	align-items: flex-start;
	display: flex;
	flex-direction: column;
	gap: 30px;
	grid-column: 1 / span 6;
	justify-content: flex-start;
	width: 100%;

	${mq.greaterThan('tablet')} {
		flex-direction: row;
		flex-wrap: wrap;
	}

	.color {
		border-radius: 3px;
		display: inline-block;
		height: 29px;
		width: 41px;
	}

	> li {
		flex-shrink: 0;
		width: 100%;

		${mq.greaterThan('tablet')} {
			width: calc(50% - 15px);
		}
	}

	h2, p {
		text-align: left;
	}

	h2 {
		margin-top: 40px;
		font-size: 30px !important;
		letter-spacing: -.1px !important;
		line-height: 24px !important;
		margin-bottom: 20px !important;

		+ div {
			margin-bottom: 30px;
		}
	}

	p {
		font-size: 18px !important;
		line-height: 24px !important;
	}

	a {
		align-items: center;
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
		justify-content: flex-start;
		width: fit-content;
	}

	.ctas {
		display: flex;
		${font18({ weight: 300 })}
		flex-direction: column;
		gap: 20px;
		margin-top: 20px;

		.title {
			font-size: 18px !important;
			font-weight: 300;
			line-height: 24px !important;
		}
	}

	.description {
		align-items: flex-start;
		display: flex;
		flex-direction: column;
		gap: 4px;
		justify-content: flex-start;
	}

	button {
		align-items: flex-start;
		display: flex;
		flex-direction: column;
		gap: 2px;

		.label {
			${createUnderline({ color: 'var(--color-text)', reverse: true })}
		}
	}

	.underline {
		${createUnderline({ color: 'var(--color-text)', reverse: true })}
	}
`